import config from './config';
import { getSessionFromCookie } from '../components/utils/cognito';

// FROZEN — spec 2026-08-19 (contract + statement permalinks) and 2026-09-29 (document permalinks).
// Interfaces mirror yuso-portals-backend §4/§5 response JSON byte-for-byte — do not reshape here.

export type ContractPermalinkType = 'injection' | 'offtake' | 'flex';

export class PermalinkError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'PermalinkError';
    this.status = status;
  }
}

export interface PermalinkCompany {
  id: number;
  kbo: string | null;
  name: string;
}

// The contract bag differs per type (§4): injection carries fixedFee/imbCostPct/windImbCostPct,
// offtake is the same minus those fee fields, flex carries asset_type/contract_status/asset_specs.
// All values may be null; snake_case aliases are read by ContractDetails through its null guards.
export type InjectionContract = {
  contract_id: number;
  installation_id: string | null;
  startDate: string | null;
  endDate: string | null;
  signatureDate: string | null;
  fixedFee: string | null;
  imbCostPct: string | null;
  windImbCostPct: string | null;
  latest: number;
  contract_row_status: string | null;
  reason_ended: string | null;
  ean: string | null;
  sdp_status: string | null;
  address_street: string | null;
  address_number: string | null;
  address_zip: number | null;
  address_city: string | null;
  latitude: string | null;
  longitude: string | null;
  product_name: string | null;
  site_id: number | null;
  site_name: string | null;
};

export type OfftakeContract = Omit<InjectionContract, 'fixedFee' | 'imbCostPct' | 'windImbCostPct'>;

export type FlexContract = {
  id: number;
  name: string | null;
  sdp_flex_id: number;
  sdp_flex_installation_id: string | null;
  product_id: number | null;
  product_name: string | null;
  asset_type: string | null;
  contract_status: string | null;
  contract_flex_id: number;
  signature_date: string | null;
  start_date: string | null;
  commencement_date: string | null;
  end_date: string | null;
  settlement_type: string | null;
  partner_revenue: string | null;
  partner_fee: string | null;
  partner_profit_share: number | null;
  yuso_profit_share: string | null;
  steering_fee_fixed: string | null;
  iot_device_id: number | null;
  asset_specs: string | Record<string, unknown> | null;
};

export type ContractPermalinkContract = InjectionContract | OfftakeContract | FlexContract;

export interface ContractPermalinkResponse {
  type: ContractPermalinkType;
  contract: ContractPermalinkContract;
  company: PermalinkCompany;
  superseded_by: number | null;
  is_latest: boolean;
}

export interface StatementPermalinkDocument {
  id: string;
  name: string;
  date: string;
  type: 'statement';
  file_name: string;
}

export interface StatementPermalinkResponse {
  url: string;
  document: StatementPermalinkDocument;
  company: PermalinkCompany;
}

export type DocumentPermalinkType = 'completed_agreement' | 'signed_mandate' | 'switch_confirmation' | 'eoc_confirmation';

export interface DocumentPermalinkDocument {
  id: string;
  name: string | null;
  date: string | null;
  ean: string | null;
  type: DocumentPermalinkType;
  signed: boolean;
  file_name: string;
}

export interface DocumentPermalinkResponse {
  url: string;
  document: DocumentPermalinkDocument;
  company: PermalinkCompany;
}

const getPortalsToken = () => getSessionFromCookie()?.getAccessToken().getJwtToken();

export const fetchContractPermalink = async (
  type: ContractPermalinkType,
  id: string,
): Promise<ContractPermalinkResponse> => {
  const response = await fetch(`${config.API_SERVICES}/api/v1/contracts/${type}/${id}`, {
    headers: { Authorization: `Bearer ${getPortalsToken()}` },
  });

  if (!response.ok) {
    throw new PermalinkError(response.status, `Failed to fetch contract: ${response.status}`);
  }

  return response.json();
};

export const fetchStatementPermalink = async (documentId: string): Promise<StatementPermalinkResponse> => {
  const response = await fetch(
    `${config.API_SERVICES}/api/v1/documents/statements/resolve/${encodeURIComponent(documentId)}`,
    {
      headers: { Authorization: `Bearer ${getPortalsToken()}` },
    },
  );

  if (!response.ok) {
    throw new PermalinkError(response.status, `Failed to resolve statement: ${response.status}`);
  }

  return response.json();
};

export const fetchDocumentPermalink = async (
  type: DocumentPermalinkType,
  documentId: string,
): Promise<DocumentPermalinkResponse> => {
  const response = await fetch(
    `${config.API_SERVICES}/api/v1/documents/resolve/${encodeURIComponent(documentId)}?type=${type}`,
    {
      headers: { Authorization: `Bearer ${getPortalsToken()}` },
    },
  );

  if (!response.ok) {
    throw new PermalinkError(response.status, `Failed to resolve document: ${response.status}`);
  }

  return response.json();
};
